import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  CheckIcon,
  XMarkIcon,
  PlusIcon,
  TrashIcon
} from '@heroicons/react/24/outline';

const ProgramSignupModal = ({ program, isOpen, onClose, onSuccess }) => {
  const { currentUser } = useAuth();
  // Get the first available fee type as default
  const getDefaultFeeType = () => {
    const fees = program?.fees || {};
    if (fees.once_weekly) return 'once_weekly';
    if (fees.twice_weekly) return 'twice_weekly';
    if (fees.term) return 'term';
    if (fees.monthly) return 'monthly';
    if (fees.ad_hoc) return 'ad_hoc';
    return 'monthly'; // fallback
  };

  // Initialize with one participant
  const [participants, setParticipants] = useState([
    {
      participant_name: '',
      participant_age: '',
      fee_type: getDefaultFeeType(),
      medical_info: ''
    }
  ]);
  
  const [formData, setFormData] = useState({
    parent_name: '',
    parent_email: '',
    parent_phone: '',
    emergency_contact: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen) {
      setParticipants([{
        participant_name: '',
        participant_age: '',
        fee_type: getDefaultFeeType(),
        medical_info: ''
      }]);
      setFormData({
        parent_name: '',
        parent_email: '',
        parent_phone: '',
        emergency_contact: ''
      });
      setError('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleParticipantChange = (index, field, value) => {
    const updated = participants.map((p, i) => 
      i === index ? { ...p, [field]: value } : p
    );
    setParticipants(updated);
  };

  const addParticipant = () => {
    setParticipants([...participants, {
      participant_name: '',
      participant_age: '',
      fee_type: getDefaultFeeType(),
      medical_info: ''
    }]);
  };

  const removeParticipant = (index) => {
    if (participants.length > 1) {
      setParticipants(participants.filter((_, i) => i !== index));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      if (!currentUser) {
        setError('Please login to continue');
        return;
      }

      const token = await currentUser.getIdToken();
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';

      // Create signup for each participant
      const signupPromises = participants.map(participant => {
        const payload = {
          ...formData,
          ...participant
        };

        return fetch(`${apiUrl}/api/programs/${program.id}/signup`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(payload)
        });
      });

      const responses = await Promise.all(signupPromises);
      const results = await Promise.all(responses.map(r => r.json()));

      // Check if all succeeded
      const allSuccess = responses.every(r => r.ok) && results.every(r => r.success);
      
      if (allSuccess) {
        const count = participants.length;
        const plural = count > 1 ? 's' : '';
        alert(`${count} participant${plural} registered successfully! You will receive an invoice at the end of the month.`);
        onSuccess();
        onClose();
        setSubmitting(false);
      } else {
        const errorMsg = results.find(r => r.error)?.error || 'Failed to process some signups';
        setError(errorMsg);
      }
    } catch (error) {
      console.error('Signup error:', error);
      setError('Failed to process signup. Please try again. Error: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const getFeeOptions = () => {
    const fees = program.fees || {};
    const options = [];

    if (fees.monthly) options.push({ value: 'monthly', label: `Monthly: ${fees.monthly}` });
    if (fees.once_weekly) options.push({ value: 'once_weekly', label: `Once Weekly: ${fees.once_weekly}` });
    if (fees.twice_weekly) options.push({ value: 'twice_weekly', label: `Twice Weekly: ${fees.twice_weekly}` });
    if (fees.ad_hoc) options.push({ value: 'ad_hoc', label: `Ad-hoc: ${fees.ad_hoc}` });
    if (fees.term) options.push({ value: 'term', label: `Term: ${fees.term}` });

    return options;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-2xl p-8 max-w-2xl w-full border border-gray-700/50 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-2xl font-bold text-white">Register for {program.name}</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-900/20 border border-red-600 rounded-lg">
            <p className="text-red-400">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Parent/Guardian Information - Shared for all participants */}
          <div className="bg-gray-700/30 rounded-lg p-4 mb-6">
            <h4 className="text-lg font-semibold text-white mb-4">Parent/Guardian Information</h4>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Parent/Guardian Name *
                </label>
                <input
                  type="text"
                  name="parent_name"
                  value={formData.parent_name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-oldham-gold"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Parent/Guardian Email *
                </label>
                <input
                  type="email"
                  name="parent_email"
                  value={formData.parent_email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-oldham-gold"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Parent/Guardian Phone *
                </label>
                <input
                  type="tel"
                  name="parent_phone"
                  value={formData.parent_phone}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-oldham-gold"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Emergency Contact
                </label>
                <input
                  type="text"
                  name="emergency_contact"
                  value={formData.emergency_contact}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-oldham-gold"
                />
              </div>
            </div>
          </div>

          {/* Participants - Can add multiple */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-lg font-semibold text-white">Participants</h4>
              <button
                type="button"
                onClick={addParticipant}
                className="flex items-center gap-2 px-4 py-2 bg-oldham-gold/20 hover:bg-oldham-gold/30 text-oldham-gold rounded-lg transition-all"
              >
                <PlusIcon className="w-5 h-5" />
                Add Another Child
              </button>
            </div>

            {participants.map((participant, index) => (
              <div key={index} className="bg-gray-700/20 rounded-lg p-6 mb-4 border border-gray-600">
                <div className="flex items-center justify-between mb-4">
                  <h5 className="text-md font-medium text-white">Child {index + 1}</h5>
                  {participants.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeParticipant(index)}
                      className="text-red-400 hover:text-red-300"
                    >
                      <TrashIcon className="w-5 h-5" />
                    </button>
                  )}
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Child's Name *
                    </label>
                    <input
                      type="text"
                      value={participant.participant_name}
                      onChange={(e) => handleParticipantChange(index, 'participant_name', e.target.value)}
                      required
                      className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-oldham-gold"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Age *
                    </label>
                    <input
                      type="number"
                      value={participant.participant_age}
                      onChange={(e) => handleParticipantChange(index, 'participant_age', e.target.value)}
                      required
                      className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-oldham-gold"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Fee Type *
                    </label>
                    <select
                      value={participant.fee_type}
                      onChange={(e) => handleParticipantChange(index, 'fee_type', e.target.value)}
                      required
                      className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-oldham-gold"
                    >
                      {getFeeOptions().map(option => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Medical Information
                    </label>
                    <textarea
                      value={participant.medical_info}
                      onChange={(e) => handleParticipantChange(index, 'medical_info', e.target.value)}
                      rows={2}
                      className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-oldham-gold"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="flex gap-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-gray-600 text-white hover:bg-gray-500 font-semibold py-3 px-6 rounded-lg transition-all"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 bg-oldham-gold text-gray-900 hover:bg-oldham-gold/90 font-semibold py-3 px-6 rounded-lg transition-all disabled:opacity-50 flex items-center justify-center"
            >
              {submitting ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-900 mr-2"></div>
                  Processing...
                </>
              ) : (
                <>
                  <CheckIcon className="w-5 h-5 mr-2" />
                  Register {participants.length > 1 ? `${participants.length} Children` : 'Child'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProgramSignupModal;
